import type { ZephyrFolder } from '@/lib/api/quality-types.js';

/** A Zephyr folder flattened into tree order with its nesting depth — the shape the folder pickers
 *  render (depth drives the indentation, same as TestRail sections). */
export interface OrderedZephyrFolder {
  id: number;
  name: string;
  depth: number;
}

/**
 * Order Zephyr folders into a depth-first tree and stamp each with its nesting depth, so the pickers
 * can indent them into a hierarchy the way TestRail sections already are. The API returns a flat list
 * carrying `parentId`; this walks it into parent→child order. Siblings keep the server's order. A
 * folder whose parent isn't in the list is treated as a root, and any folder unreachable through the
 * parent chain (a cycle) is appended at depth 0 so nothing is silently dropped.
 */
export function orderZephyrFolders(
  folders: ZephyrFolder[],
): OrderedZephyrFolder[] {
  const byId = new Map(folders.map((f) => [f.id, f]));
  const childrenByParent = new Map<number | null, ZephyrFolder[]>();
  for (const folder of folders) {
    const parentKey =
      folder.parentId != null && byId.has(folder.parentId)
        ? folder.parentId
        : null;
    const siblings = childrenByParent.get(parentKey) ?? [];
    siblings.push(folder);
    childrenByParent.set(parentKey, siblings);
  }

  const ordered: OrderedZephyrFolder[] = [];
  const visited = new Set<number>();
  const walk = (parentKey: number | null, depth: number): void => {
    for (const folder of childrenByParent.get(parentKey) ?? []) {
      if (visited.has(folder.id)) continue; // cycle guard
      visited.add(folder.id);
      ordered.push({ id: folder.id, name: folder.name, depth });
      walk(folder.id, depth + 1);
    }
  };
  walk(null, 0);

  for (const folder of folders) {
    if (!visited.has(folder.id))
      ordered.push({ id: folder.id, name: folder.name, depth: 0 });
  }
  return ordered;
}
